import { useEffect, useMemo, useState } from "react";
import { Users, Car, ShieldCheck, MapPin } from "lucide-react";
import { ScrollSection, ScrollReveal, Counter } from "@/components/animations";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  fetchPublicPlatformStats,
  formatStatWithPlus,
  type PublicPlatformStats,
} from "@/lib/services/platformStats.service";

const EMPTY_STATS: PublicPlatformStats = {
  partnerAgencies: 0,
  availableVehicles: 0,
  coveredCities: 0,
  verifiedProfiles: 0,
};

const Stats = () => {
  const { t } = useTranslation();
  const [liveStats, setLiveStats] = useState<PublicPlatformStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isCancelled = false;

    const loadStats = async () => {
      setIsLoading(true);
      const stats = await fetchPublicPlatformStats();

      if (!isCancelled) {
        setLiveStats(stats);
        setIsLoading(false);
      }
    };

    loadStats();

    return () => {
      isCancelled = true;
    };
  }, []);

  const resolvedStats = liveStats ?? EMPTY_STATS;

  const stats = useMemo(
    () => [
      {
        icon: Users,
        value: formatStatWithPlus(resolvedStats.partnerAgencies),
        label: t("stats.partnerAgencies"),
      },
      {
        icon: Car,
        value: formatStatWithPlus(resolvedStats.availableVehicles),
        label: t("stats.availableVehicles"),
      },
      {
        icon: MapPin,
        value: String(resolvedStats.coveredCities),
        label: t("stats.coveredCities"),
      },
      {
        icon: ShieldCheck,
        value: formatStatWithPlus(resolvedStats.verifiedProfiles),
        label: t("stats.verifiedProfiles"),
      },
    ],
    [resolvedStats, t]
  );

  return (
    <ScrollSection id="stats" className="relative overflow-hidden bg-scandi-bg py-8 sm:py-10">
      <motion.div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div className="mb-5 text-center">
          <ScrollReveal direction="up" distance={20}>
            <span className="rakb-section-eyebrow mb-2 inline-block">{t("stats.subtitle")}</span>
            <h2 className="rakb-section-title mx-auto max-w-xl text-2xl sm:text-3xl">
              {t("stats.title")}
            </h2>
          </ScrollReveal>
          <ScrollReveal direction="up" distance={15} delay={0.1}>
            <p className="mx-auto mt-2 max-w-2xl text-sm text-text-secondary">
              {t("stats.description")}
            </p>
          </ScrollReveal>
        </motion.div>

        <div className="grid grid-cols-2 overflow-hidden rounded-2xl border border-border/70 bg-white md:grid-cols-4">
          {stats.map((stat, index) => (
            <div
              key={stat.label}
              className={`flex flex-col items-center px-3 py-5 text-center sm:px-4 sm:py-6 ${
                index % 2 === 0 ? "border-e border-border/70" : ""
              } ${index < 2 ? "border-b border-border/70 md:border-b-0" : ""} ${
                index > 0 && index < 3 ? "md:border-e md:border-border/70" : ""
              }`}
            >
              <stat.icon className="mb-2 h-4 w-4 text-text-tertiary" aria-hidden />
              {isLoading ? (
                <div
                  className="mb-1 h-8 w-14 animate-pulse rounded-md bg-muted"
                  aria-hidden="true"
                />
              ) : (
                <Counter
                  value={stat.value}
                  className="font-heading text-2xl font-semibold text-text-primary sm:text-3xl"
                />
              )}
              <p className="mt-1 text-xs text-text-secondary sm:text-sm">{stat.label}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </ScrollSection>
  );
};

export default Stats;
